(() => {
  const endpoint = 'https://cdn.judge.me/reviews/all_reviews_js_based';
  const pageSize = 25;
  const previousReviewsKey = 'cacb-confidence-review-ids';
  const cardLoaders = new WeakMap();

  const fallbackReviews = [
    { quote: 'I will always come back and look for new additions.', author: 'Jason in AZ', theme: 'Trust · repeat purchase', rating: 5, verified: true },
    { quote: 'Restored to perfection. This is my new daily pan.', author: 'Meredith', theme: 'Restoration quality · ready to cook', rating: 5, verified: true },
    { quote: 'Beautifully restored and very well packaged.', author: 'Andrea', theme: 'Restoration quality · packaging', rating: 5, verified: true },
    { quote: 'Exactly as described and perfectly prepared for use.', author: 'John', theme: 'Reliable source · ready to cook', rating: 5, verified: true },
    { quote: 'Even more beautiful than the photos online.', author: 'Nicholas', theme: 'Better than photos · packaging', rating: 5, verified: true },
    { quote: 'I’ve bought 5 pieces … They are all heirloom pieces.', author: 'G.', theme: 'Repeat purchase · legacy', rating: 5, verified: true },
  ];

  const randomInteger = (maximum) => {
    if (maximum <= 1) return 0;
    if (window.crypto?.getRandomValues) {
      const values = new Uint32Array(1);
      window.crypto.getRandomValues(values);
      return values[0] % maximum;
    }
    return Math.floor(Math.random() * maximum);
  };

  const shuffledIndexes = (length) => {
    const indexes = Array.from({ length }, (_, index) => index);
    for (let index = indexes.length - 1; index > 0; index -= 1) {
      const swapIndex = randomInteger(index + 1);
      [indexes[index], indexes[swapIndex]] = [indexes[swapIndex], indexes[index]];
    }
    return indexes;
  };

  const readPreviousIds = () => {
    try {
      return JSON.parse(sessionStorage.getItem(previousReviewsKey) || '[]');
    } catch (error) {
      return [];
    }
  };

  const rememberIds = (reviews) => {
    try {
      sessionStorage.setItem(previousReviewsKey, JSON.stringify(reviews.map((review) => review.id)));
    } catch (error) {
      // Storage is optional; the pool selection remains random without it.
    }
  };

  const reviewUrl = (reviewType, page) => {
    const shopDomain = window.Shopify?.shop || '94578c-3.myshopify.com';
    const params = new URLSearchParams({
      url: shopDomain,
      shop_domain: shopDomain,
      platform: 'shopify',
      page: String(page),
      review_type: reviewType,
    });
    return `${endpoint}?${params}`;
  };

  const fetchPage = async (reviewType, page) => {
    const response = await fetch(reviewUrl(reviewType, page), {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Judge.me returned ${response.status}`);
    return response.json();
  };

  const parsePage = (html) => {
    const parsed = new DOMParser().parseFromString(html || '', 'text/html');
    return Array.from(parsed.querySelectorAll('.jdgm-rev'));
  };

  const themeFor = (text) => {
    const value = text.toLowerCase();
    const themes = [];
    if (/restor|season|condition|beautiful|quality/.test(value)) themes.push('Restoration quality');
    if (/pack|ship|arriv|delivery/.test(value)) themes.push('Protected packaging');
    if (/cook|daily|use|ready/.test(value)) themes.push('Ready to cook');
    if (/describ|photo|picture|expected/.test(value)) themes.push('As described');
    if (/again|more|repeat|always|return/.test(value)) themes.push('Repeat purchase');
    return (themes.length ? themes : ['Customer experience']).slice(0, 2).join(' · ');
  };

  const reviewFromNode = (node) => {
    if (node?.dataset.verifiedBuyer !== 'true') return null;
    const body = node.querySelector('.jdgm-rev__body')?.textContent.trim();
    const title = node.querySelector('.jdgm-rev__title')?.textContent.trim();
    const author = node.querySelector('.jdgm-rev__author')?.textContent.trim() || 'Judge.me customer';
    const quote = body || title;
    if (!quote) return null;
    const rating = Math.max(1, Math.min(5, Number.parseInt(node.querySelector('.jdgm-rev__rating')?.dataset.score || '5', 10)));
    return {
      id: node.dataset.reviewId || `${author}:${quote}`,
      quote,
      author,
      theme: themeFor(`${title || ''} ${body || ''}`),
      rating,
      verified: true,
    };
  };

  const selectReviews = async (quantity) => {
    const firstShopPage = await fetchPage('shop-reviews', 1);
    const shopCount = Number.parseInt(firstShopPage.number_of_shop_reviews || '0', 10);
    const productCount = Number.parseInt(firstShopPage.number_of_product_reviews || '0', 10);
    const totalCount = shopCount + productCount;
    if (totalCount <= 0) throw new Error('Judge.me returned an empty review pool');

    const previousIds = new Set(readPreviousIds());
    const requests = new Map([['shop-reviews:1', Promise.resolve(firstShopPage)]]);
    const desiredPageCount = Math.min(4, Math.ceil(shopCount / pageSize) + Math.ceil(productCount / pageSize));
    while (requests.size < desiredPageCount) {
      const globalIndex = randomInteger(totalCount);
      const reviewType = globalIndex < shopCount ? 'shop-reviews' : 'product-reviews';
      const typeIndex = reviewType === 'shop-reviews' ? globalIndex : globalIndex - shopCount;
      const page = Math.floor(typeIndex / pageSize) + 1;
      const key = `${reviewType}:${page}`;
      if (!requests.has(key)) {
        requests.set(key, fetchPage(reviewType, page));
      }
    }

    const payloads = await Promise.all(requests.values());
    const resolved = payloads.flatMap((payload) => parsePage(payload.html).map(reviewFromNode).filter(Boolean));
    const distinct = Array.from(new Map(resolved.map((review) => [review.id, review])).values());
    const randomized = shuffledIndexes(distinct.length).map((index) => distinct[index]);
    const fresh = randomized.filter((review) => !previousIds.has(review.id));
    const selection = [...fresh, ...randomized.filter((review) => previousIds.has(review.id))].slice(0, quantity);
    if (selection.length < quantity) throw new Error('Judge.me returned too few displayable reviews');
    rememberIds(selection);
    return { reviews: selection, totalCount };
  };

  const render = (card, reviews, totalCount) => {
    const slots = Array.from(card.querySelectorAll('[data-confidence-review]'));
    slots.forEach((slot, slotIndex) => {
      const review = reviews[slotIndex % reviews.length];
      const quote = slot.querySelector('[data-confidence-quote]');
      const author = slot.querySelector('[data-confidence-author]');
      const theme = slot.querySelector('[data-confidence-theme]');
      if (quote) quote.textContent = `“${review.quote}”`;
      if (author) author.textContent = `${review.author} · verified customer`;
      if (theme) theme.textContent = review.theme;
    });
    const stars = card.querySelector('.cacb-trust__story .cacb-trust__stars');
    if (stars) {
      const rating = reviews[0].rating || 5;
      stars.textContent = `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}`;
      stars.setAttribute('aria-label', `${rating} out of 5 stars`);
    }
    const trust = card.closest('.cacb-trust--product');
    const count = trust?.querySelector('.cacb-trust__review-count strong');
    if (count && totalCount) count.textContent = String(totalCount);
    card.dataset.confidenceSource = totalCount ? 'judgeme-verified-pool' : 'embedded-verified-fallback';
    card.dataset.confidenceReviewIds = reviews.map((review) => review.id || review.author).join(',');
  };

  const initializeCard = (card) => {
    if (card.dataset.confidenceReady === 'true' && cardLoaders.has(card)) return;
    card.dataset.confidenceReady = 'true';
    const button = card.querySelector('[data-confidence-next]');

    const load = async () => {
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      try {
        const selection = await selectReviews(2);
        render(card, selection.reviews, selection.totalCount);
      } catch (error) {
        const indexes = shuffledIndexes(fallbackReviews.length).slice(0, 2);
        render(card, indexes.map((index) => fallbackReviews[index]), null);
      } finally {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    };

    cardLoaders.set(card, () => {
      load();
    });
    load();
  };

  const initializeAll = (root = document) => root.querySelectorAll('[data-cacb-confidence-card]').forEach(initializeCard);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  else initializeAll();
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-confidence-next]');
    const card = button?.closest('[data-cacb-confidence-card]');
    if (!card) return;
    const loader = cardLoaders.get(card);
    if (loader) loader();
    else {
      card.dataset.confidenceReady = 'false';
      initializeCard(card);
    }
  });
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
